'use client';

/* Hallmark · genre: editorial · macrostructure: Narrative Workflow — R8 · theme: custom (warm ledger) · design-system: R8 contract */

import { cn } from '@/lib/utils';
import { useLocale } from '../hooks/useLocale';
import { useReducedMotion } from '../hooks/useMediaQuery';
import { useScrollReveal } from '@/features/preview/hooks/useScrollReveal';
import { LANDING_CONTENT } from '../constants/content';
import { PAPER_BAND, PAPER_INK, PAPER_RULE, PAPER_ACCENT_INK } from '../ui/paper-tokens';
import { CosmosMark } from '../ui/CosmosMark';
import LandingReveal from './LandingReveal';
import {
  LANDING_MOTION_MS,
  LANDING_EASE_CSS,
  LANDING_INVIEW,
  LANDING_STAGGER_MS,
} from '../motion/landing-motion';

/**
 * BrainSystemSection — R8 restructure §B ("Everything your team does lives
 * in one system"), spec of record `.claude/handoffs/2026-08-12-r8-
 * restructure-contract.md`. New section order slot #5 (right after
 * OneProcessSection). Owns its own root band paint — `PAPER_BAND.alt` per
 * the contract's band-ladder proposal ("Brain alt").
 *
 * GEOMETRY (2026-08-12, R8 review FIX 3 — replaces the first pass's single
 * vertical "spine"). The contract's §B is explicit and this is Albert's
 * centrepiece: the seven feature nodes CONVERGE into the Cosmos core. A flat
 * stacked list threaded by one line is a list, not a convergence — it draws
 * the same picture whether the core sits at the end or is absent entirely.
 * The layout is now hand-authored static geometry in a fixed viewBox, with
 * ZERO runtime measurement (the deleted `hero-journey-geometry.ts` is the
 * house precedent for that discipline, and it is what keeps this jsdom-safe):
 *
 *   desktop (md+): a 5-column grid — 4 node labels down the LEFT, 3 down the
 *     RIGHT (deliberately staggered against each other), a fixed-width core
 *     column between them, and two narrow CONNECTOR GUTTERS either side of
 *     the core carrying the curved hairlines. Vertical positions are EXACT,
 *     not measured: the grid declares 12 equal rows, left nodes span 3 rows
 *     each and right nodes span 4, so a left node's centre always lands at
 *     12.5/37.5/62.5/87.5% of the diagram height and a right node's at
 *     16.67/50/83.33% — whatever the label happens to wrap to. Each gutter
 *     SVG uses `viewBox="0 0 100 100"` + `preserveAspectRatio="none"`, so
 *     those same percentages ARE its path coordinates: every curve starts on
 *     its node's own centreline and ends at (core edge, 50). The gutter is
 *     its own grid column precisely so that no path coordinate ever depends
 *     on how wide the flexible label columns resolve to.
 *   mobile (<md): the SAME seven label elements reflow into a 2-column grid
 *     (one DOM, CSS-only — never a second copy of the labels, which would
 *     duplicate every string for assistive tech and for `getByText`), and a
 *     short two-stub merge figure funnels them into the core rendered below.
 *
 * MOTION: unchanged in kind from the first pass. `pathLength={1}` normalizes
 * stroke-dasharray/-dashoffset math to a fixed [0,1] range regardless of a
 * path's real pixel length — this sidesteps `SVGGeometryElement.
 * getTotalLength()` entirely, which `BigStatsMotion.tsx`'s own doc comment
 * flags as "unavailable in this repo's jsdom test environment (no polyfill on
 * disk)". `vector-effect="non-scaling-stroke"` keeps the hairline width crisp
 * under the `preserveAspectRatio="none"` stretch. The draw is triggered ONCE
 * via `useScrollReveal` (the same proven IntersectionObserver hook
 * `LandingReveal.tsx` is built on) at `LANDING_INVIEW.connector`'s geometry
 * (this app's own "desktop connector draw (once)" trigger contract) and
 * timed off `LANDING_MOTION_MS.demoStep` (520ms, documented as "Connector
 * draw and one discrete demo action") + `LANDING_EASE_CSS.enter`, with a
 * per-path `LANDING_STAGGER_MS` offset so the seven lines arrive as one
 * sweep inward rather than a synchronized flash — no literal duration/easing
 * at an `animate()`-shaped call site (`check-landing-choreography.mjs`); this
 * is a plain CSS transition driven by a style object, not a `.animate()`/
 * `inView()` call, so it is outside that check's scope entirely, and named
 * tokens are still used throughout for single-source-of-truth pacing.
 * `prefers-reduced-motion` short-circuits `drawn` to `true` unconditionally
 * (no transition ever scheduled) — the "reduced-motion static" contract
 * requirement — backed by a CSS `motion-reduce:transition-none`
 * counter-class as a second, independent layer.
 *
 * INK: hairlines are `PAPER_RULE.hairline`, labels the `PAPER_INK` ladder.
 * Amber appears ONCE, on the core mark (`PAPER_ACCENT_INK`, the contrast-
 * proven readable-amber-as-glyph token) — the single budgeted accent this
 * section is allowed, never on a node.
 */

/** Vertical centre of each LEFT node, as a percentage of the diagram box —
 * the exact centre of a 3-row span inside a 12-row grid (rows 1-3, 4-6, 7-9,
 * 10-12). Hand-authored to match the grid, never measured from the DOM. */
const LEFT_NODE_Y = [12.5, 37.5, 62.5, 87.5] as const;
/** Same, for the RIGHT nodes: the centre of a 4-row span (1-4, 5-8, 9-12). */
const RIGHT_NODE_Y = [100 / 6, 50, 500 / 6] as const;

/** Explicit md+ grid placement per node index. Written as whole literal class
 * strings (never interpolated) because Tailwind's scanner is static — an
 * `md:row-start-${n}` template would compile to nothing. Indices 0-3 are the
 * left column (grid column 1), 4-6 the right (grid column 5). */
const NODE_PLACEMENT = [
  'md:col-start-1 md:row-start-1 md:row-span-3 md:justify-end md:text-right',
  'md:col-start-1 md:row-start-4 md:row-span-3 md:justify-end md:text-right',
  'md:col-start-1 md:row-start-7 md:row-span-3 md:justify-end md:text-right',
  'md:col-start-1 md:row-start-10 md:row-span-3 md:justify-end md:text-right',
  'md:col-start-5 md:row-start-1 md:row-span-4 md:justify-start md:text-left',
  'md:col-start-5 md:row-start-5 md:row-span-4 md:justify-start md:text-left',
  'md:col-start-5 md:row-start-9 md:row-span-4 md:justify-start md:text-left',
] as const;

/** The shared line-draw style for every hairline in this diagram — one place
 * the dash/transition contract is expressed, so a gutter path and a mobile
 * stub can never drift apart. */
function drawStyle(drawn: boolean, reduced: boolean, index: number) {
  return {
    strokeDasharray: 1,
    strokeDashoffset: drawn ? 0 : 1,
    transition: reduced
      ? undefined
      : `stroke-dashoffset ${LANDING_MOTION_MS.demoStep}ms ${LANDING_EASE_CSS.enter}`,
    transitionDelay: reduced ? undefined : `${index * LANDING_STAGGER_MS}ms`,
  };
}

/**
 * One converging hairline, drawn in its gutter's own 0-100 coordinate space.
 * `fromX`/`toX` are the gutter edges (0 -> 100 left of the core, 100 -> 0
 * right of it); the control points hold the node's own Y for the first part
 * of the run, then bend to the core's Y — a quiet single curve, never a
 * diagonal.
 */
function ConvergingPath({
  y,
  fromX,
  toX,
  drawn,
  reduced,
  index,
}: {
  y: number;
  fromX: number;
  toX: number;
  drawn: boolean;
  reduced: boolean;
  index: number;
}) {
  const mid1 = fromX + (toX - fromX) * 0.45;
  const mid2 = fromX + (toX - fromX) * 0.55;
  return (
    <path
      d={`M ${fromX} ${y} C ${mid1} ${y}, ${mid2} 50, ${toX} 50`}
      fill="none"
      pathLength={1}
      vectorEffect="non-scaling-stroke"
      stroke={PAPER_RULE.hairline.hex}
      strokeWidth={1}
      className="motion-reduce:transition-none"
      style={drawStyle(drawn, reduced, index)}
    />
  );
}

/** The desktop connector layer for ONE side. Its own grid column, so its
 * 0-100 x-axis spans exactly the gap between the label column and the core. */
function ConnectorGutter({
  side,
  ys,
  drawn,
  reduced,
  indexOffset,
  className,
}: {
  side: 'left' | 'right';
  ys: readonly number[];
  drawn: boolean;
  reduced: boolean;
  indexOffset: number;
  className: string;
}) {
  const fromX = side === 'left' ? 0 : 100;
  const toX = side === 'left' ? 100 : 0;
  return (
    <svg
      aria-hidden="true"
      focusable="false"
      data-slot={`connectors-${side}`}
      viewBox="0 0 100 100"
      preserveAspectRatio="none"
      className={cn('pointer-events-none h-full w-full', className)}
    >
      {ys.map((y, i) => (
        <ConvergingPath
          key={y}
          y={y}
          fromX={fromX}
          toX={toX}
          drawn={drawn}
          reduced={reduced}
          index={indexOffset + i}
        />
      ))}
    </svg>
  );
}

/** The mobile merge figure: two short stubs, one under each grid column,
 * funnelling into the core rendered directly below. Never shown at md+. */
function MobileMerge({ drawn, reduced }: { drawn: boolean; reduced: boolean }) {
  return (
    <svg
      aria-hidden="true"
      focusable="false"
      data-slot="connectors-mobile"
      viewBox="0 0 100 24"
      preserveAspectRatio="none"
      className="pointer-events-none h-6 w-full"
    >
      {[25, 75].map((x, i) => (
        <path
          key={x}
          d={`M ${x} 0 C ${x} 14, 50 8, 50 24`}
          fill="none"
          pathLength={1}
          vectorEffect="non-scaling-stroke"
          stroke={PAPER_RULE.hairline.hex}
          strokeWidth={1}
          className="motion-reduce:transition-none"
          style={drawStyle(drawn, reduced, i)}
        />
      ))}
    </svg>
  );
}

function BrainSystemSectionInner() {
  const { t } = useLocale();
  const { brainSystem } = LANDING_CONTENT;
  const nodes = brainSystem.nodes;
  const reduced = useReducedMotion();
  const { ref: diagramRef, isVisible } = useScrollReveal<HTMLDivElement>({
    threshold: LANDING_INVIEW.connector.amount,
    rootMargin: LANDING_INVIEW.connector.margin,
  });
  const drawn = reduced || isVisible;

  return (
    <section
      id="brain-system"
      data-landing-chapter="brain-system"
      className={cn('relative overflow-x-clip py-16 lg:py-20', PAPER_BAND.alt.bg)}
    >
      <div className="relative z-10 mx-auto max-w-[1280px] px-5 sm:px-6 lg:px-8">
        <LandingReveal>
          <div className="mb-10 max-w-2xl lg:mb-12">
            <span
              className={cn(
                'mb-4 block text-[11px] font-semibold uppercase leading-4 tracking-[0.12em]',
                PAPER_INK.muted.text
              )}
            >
              {t(brainSystem.eyebrow)}
            </span>
            <h2
              className={cn(
                'landing-serif-display max-w-[18ch] text-[28px] leading-[1.15] lg:text-[34px] lg:leading-[1.1]',
                PAPER_INK.primary.text
              )}
            >
              {t(brainSystem.h2)}
            </h2>
            <p
              className={cn(
                'mt-3 max-w-[52ch] text-[15px] leading-[1.55]',
                PAPER_INK.secondary.text
              )}
            >
              {t(brainSystem.sub)}
            </p>
          </div>

          <div
            ref={diagramRef}
            data-slot="brain-diagram"
            className={cn(
              // Mobile: two columns of quiet labels. md+: label / gutter /
              // core / gutter / label over 12 equal rows — those 12 rows are
              // what makes the SVG coordinates exact without measuring
              // anything (see the file header's GEOMETRY note).
              'mx-auto grid max-w-md grid-cols-2 gap-x-6 gap-y-5',
              'md:max-w-4xl md:grid-cols-[minmax(0,1fr)_48px_minmax(0,200px)_48px_minmax(0,1fr)]',
              'md:min-h-[380px] md:grid-rows-[repeat(12,minmax(0,1fr))] md:gap-x-0 md:gap-y-0'
            )}
          >
            {nodes.map((node, i) => (
              <div
                key={node.id}
                data-slot="node"
                className={cn('flex items-center md:px-4', NODE_PLACEMENT[i] ?? NODE_PLACEMENT[0])}
              >
                <span
                  className={cn(
                    'text-[13px] leading-[1.4] sm:text-[14px]',
                    PAPER_INK.secondary.text
                  )}
                >
                  {t(node.label)}
                </span>
              </div>
            ))}

            <ConnectorGutter
              side="left"
              ys={LEFT_NODE_Y}
              drawn={drawn}
              reduced={reduced}
              indexOffset={0}
              className="hidden md:col-start-2 md:row-span-12 md:row-start-1 md:block"
            />
            <ConnectorGutter
              side="right"
              ys={RIGHT_NODE_Y}
              drawn={drawn}
              reduced={reduced}
              indexOffset={LEFT_NODE_Y.length}
              className="hidden md:col-start-4 md:row-span-12 md:row-start-1 md:block"
            />

            {/* Mobile-only merge into the core below. `md:hidden` removes it
                from the grid entirely at md+, so it never disturbs the
                explicit 5-column placement above. */}
            <div className="col-span-2 md:hidden">
              <MobileMerge drawn={drawn} reduced={reduced} />
            </div>

            {/* The core. Mobile: full width under the merge stubs. md+: the
                centre column, vertically centred across all 12 rows — the
                single point all seven hairlines terminate on. */}
            <div
              data-slot="core"
              className={cn(
                'col-span-2 flex flex-col items-center gap-2 text-center',
                'md:col-span-1 md:col-start-3 md:row-span-12 md:row-start-1 md:justify-center'
              )}
            >
              <span
                aria-hidden="true"
                className={cn(
                  'flex h-11 w-11 items-center justify-center rounded-full border',
                  PAPER_RULE.hairline.border,
                  PAPER_BAND.base.bg
                )}
              >
                <CosmosMark size={22} className={PAPER_ACCENT_INK.text} />
              </span>
              <span className={cn('text-[14px] font-semibold', PAPER_INK.primary.text)}>
                {t(brainSystem.core.name)}
              </span>
              <span className={cn('max-w-[34ch] text-[13px] leading-[1.6]', PAPER_INK.muted.text)}>
                {t(brainSystem.core.line)}
              </span>
            </div>
          </div>
        </LandingReveal>
      </div>
    </section>
  );
}

export default function BrainSystemSection() {
  return <BrainSystemSectionInner />;
}
